import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiGrid,
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiInfo,
  FiRefreshCw,
  FiLayers,
} from "react-icons/fi";
import {
  getPartnerServicesMySpace,
  getPartnerServiceTypes,
  addPartnerService,
  editPartnerService,
  deletePartnerService,
  getPartnerServiceDetails,
  uploadMediaFiles,
} from "../../../api/partner/partnerFeatureApis";

export default function PartnerServicesHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;
  const partnerId = authState?.user?.userDetails?.id || authState?.user?.id;

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Add / Edit Modal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [priceUnit, setPriceUnit] = useState("project");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Details Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Fetch Services
  const fetchServices = useCallback(async () => {
    if (!token || !partnerId) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      const data = await getPartnerServicesMySpace(token, partnerId);
      const items = data?.allServices || data?.services || [];
      setServices(items);
    } catch (err) {
      console.error("Failed to load partner services:", err);
      setAlertMsg({ type: "danger", text: "Failed to load services portfolio. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [token, partnerId]);

  // Fetch Service Categories
  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getPartnerServiceTypes(token);
      setCategories(data?.allServiceCategories || []);
    } catch (err) {
      console.error("Failed to load service categories:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, [fetchServices, fetchCategories]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditingServiceId(null);
    setSelectedCategoryId(categories[0]?.id ? String(categories[0].id) : "");
    setTitle("");
    setDescription("");
    setPrice("");
    setPriceUnit("project");
    setSelectedFiles([]);
    setExistingImages([]);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (service) => {
    setIsEditMode(true);
    setEditingServiceId(service.id);
    setSelectedCategoryId(
      service.all_service_category_id || service.allServiceCategory?.id
        ? String(service.all_service_category_id || service.allServiceCategory?.id)
        : ""
    );
    setTitle(service.title || "");
    setDescription(service.description || "");
    setPrice(service.price ? String(service.price).replace(/[^\d]/g, "") : "");
    setPriceUnit(service.price_unit || "project");
    setSelectedFiles([]);
    setExistingImages(service.service_photos || service.photos || []);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Delete Service
  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to permanently delete this service offering?")) {
      return;
    }
    setActionLoadingId(serviceId);
    try {
      const res = await deletePartnerService(token, serviceId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Service deleted successfully." });
        setServices((prev) => prev.filter((s) => s.id !== serviceId));
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to delete service." });
      }
    } catch (err) {
      setAlertMsg({ type: "danger", text: err?.response?.data?.text || "Failed to delete service." });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open Details Modal
  const handleOpenDetails = async (serviceId) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const data = await getPartnerServiceDetails(token, serviceId);
      setSelectedService(data?.allService || data?.service || null);
    } catch (err) {
      console.error("Failed to load service details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!title.trim() || !selectedCategoryId || !price) {
      setFormError("Please enter service title, category, and price.");
      return;
    }

    if (!isEditMode && selectedFiles.length === 0) {
      setFormError("Please upload at least one image of your work.");
      return;
    }

    setSubmitting(true);
    try {
      let uploadedImages = [];
      if (selectedFiles.length > 0) {
        const uploadRes = await uploadMediaFiles(token, selectedFiles);
        const propImages = uploadRes?.propertyImages || [];
        uploadedImages = propImages.map((img) => ({
          imagePath: img.imagePath,
          image_url: img.image_url,
          url: img.image_url,
          home: false,
          active: true,
          deleted: false,
        }));
      }

      const allPhotos = isEditMode
        ? [...existingImages, ...uploadedImages]
        : uploadedImages;

      const numericPrice = price.replace(/[^\d]/g, "");

      const payload = {
        title: title.trim(),
        description: description.trim(),
        price: numericPrice,
        price_unit: priceUnit.trim() || "project",
        all_service_category_id: Number(selectedCategoryId),
        service_photos: allPhotos,
      };

      if (isEditMode) {
        payload.id = editingServiceId;
        const res = await editPartnerService(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "Service updated successfully!" });
          setShowAddEditModal(false);
          fetchServices();
        } else {
          setFormError(res?.text || "Failed to update service.");
        }
      } else {
        const res = await addPartnerService(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "New service published successfully!" });
          setShowAddEditModal(false);
          fetchServices();
        } else {
          setFormError(res?.text || "Failed to publish service.");
        }
      }
    } catch (err) {
      setFormError(err?.response?.data?.text || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered Services
  const filteredServices = services.filter((item) => {
    if (selectedCategoryFilter && String(item.all_service_category_id) !== String(selectedCategoryFilter)) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const t = (item.title || "").toLowerCase();
    const d = (item.description || "").toLowerCase();
    return t.includes(q) || d.includes(q);
  });

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiLayers className="text-primary" /> Service Provider Hub
          </h3>
          <p className="text-muted mb-0 small">
            Manage your professional home service listings, packages, rates, and client portfolio.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
            onClick={fetchServices}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2 btn-sm px-3 shadow-sm"
            onClick={handleOpenAdd}
          >
            <FiPlus /> Add New Service
          </button>
        </div>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-4">
              <select
                className="form-select form-select-sm"
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              >
                <option value="">All Service Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-8">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search services by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading services...</span>
          </div>
          <p className="text-muted mt-2 small">Loading your professional services...</p>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiLayers size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Services Found</h5>
            <p className="text-muted small mb-3">
              {searchQuery
                ? `No services matching "${searchQuery}".`
                : "You haven't added any professional services yet. Click 'Add New Service' to create your service listings."}
            </p>
            <button className="btn btn-primary btn-sm" onClick={handleOpenAdd}>
              Add Service
            </button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filteredServices.map((item) => {
            const photos = item.service_photos || item.photos || [];
            const thumbnail = photos[0]?.url || photos[0]?.image_url
              ? ((photos[0]?.url || photos[0]?.image_url).startsWith("http")
                  ? photos[0]?.url || photos[0]?.image_url
                  : `https://images.allhomedesire.com${photos[0]?.url || photos[0]?.image_url}`)
              : null;
            const isBusy = actionLoadingId === item.id;
            const priceVal = item.price ? Number(item.price).toLocaleString("en-IN") : "0";

            return (
              <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden d-flex flex-column justify-content-between">
                  <div>
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt="Service"
                        className="w-100 object-fit-cover"
                        style={{ height: "160px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center text-muted"
                        style={{ height: "160px" }}
                      >
                        <FiGrid size={36} />
                      </div>
                    )}

                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="badge bg-primary bg-opacity-10 text-primary small">
                          {item.allServiceCategory?.name || "Service"}
                        </span>
                      </div>

                      <h6 className="fw-bold text-dark text-truncate mb-1" title={item.title}>
                        {item.title}
                      </h6>

                      <p
                        className="text-muted small mb-2 line-clamp-2"
                        style={{ minHeight: "36px" }}
                      >
                        {item.description || "Professional services for your home."}
                      </p>

                      <div className="fw-bold text-primary fs-5 mb-0">
                        ₹{priceVal}{" "}
                        <span className="text-muted small fw-normal fs-6">
                          /{item.price_unit || "project"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 pt-0 border-top mt-2">
                    <div className="d-flex gap-2 pt-2">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-grow-1"
                        onClick={() => handleOpenDetails(item.id)}
                      >
                        <FiInfo size={13} /> View
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm flex-grow-1"
                        onClick={() => handleOpenEdit(item)}
                      >
                        <FiEdit size={13} /> Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm px-3"
                        onClick={() => handleDeleteService(item.id)}
                        disabled={isBusy}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          ADD / EDIT SERVICE MODAL
          ========================================================================= */}
      {showAddEditModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleSubmit}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiLayers className="text-primary" />
                    {isEditMode ? "Edit Service Offering" : "Add New Professional Service"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  />
                </div>

                <div className="modal-body p-4">
                  {formError && (
                    <div className="alert alert-danger py-2 small mb-3">{formError}</div>
                  )}

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Service Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Architectural Planning / Modular Kitchen Setup"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Service Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedCategoryId}
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Price (INR) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 5000"
                        value={price}
                        onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Price Unit (e.g. project, hour, day, visit)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="project"
                        value={priceUnit}
                        onChange={(e) => setPriceUnit(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Service Description
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Detail the deliverables, tools used, guarantees, and timeline..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Portfolio / Service Photos {!isEditMode && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*"
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={submitting}
                  >
                    {submitting ? "Uploading & Saving..." : isEditMode ? "Save Changes" : "Publish Service"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SERVICE DETAILS MODAL
          ========================================================================= */}
      {showDetailsModal && selectedService && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {selectedService.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                {detailsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <p className="text-muted small mt-2">Loading details...</p>
                  </div>
                ) : (
                  <div>
                    {selectedService.service_photos?.length > 0 && (
                      <div className="d-flex gap-2 overflow-auto pb-2 mb-3">
                        {selectedService.service_photos.map((p, i) => (
                          <img
                            key={i}
                            src={p.url?.startsWith("http") ? p.url : `https://images.allhomedesire.com${p.url}`}
                            alt="Service"
                            className="rounded-3 object-fit-cover shadow-sm"
                            style={{ width: "220px", height: "150px" }}
                          />
                        ))}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="badge bg-primary bg-opacity-10 text-primary fs-6">
                        {selectedService.allServiceCategory?.name || "Home Service"}
                      </span>
                      <h4 className="fw-bold text-primary mb-0">
                        ₹{Number(selectedService.price || 0).toLocaleString("en-IN")}{" "}
                        <span className="text-muted small fs-6">
                          /{selectedService.price_unit || "project"}
                        </span>
                      </h4>
                    </div>

                    <h6 className="fw-bold text-dark mb-1">Description</h6>
                    <p className="text-secondary small mb-3" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedService.description || "No description provided."}
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
